import type { MedioPagoReserva } from '@padel/domain';
/**
 * Formateo para la pantalla, en espanol de Uruguay.
 *
 * Todo lo de aca es **presentacion**: no decide nada de negocio. El precio llega del
 * servidor en centesimos y se reconstruye con `crearCentesimos`, que valida, en vez de
 * castearlo (docs/MODELO_DATOS.md §9): un importe corrupto tiene que notarse al
 * mostrarlo, no al cobrarlo.
 *
 * Los nombres de dia y mes se escriben a mano y no con `Intl` a proposito: el resultado
 * de `Intl` cambia entre versiones de ICU, y una fecha que se lee distinto segun el
 * dispositivo es exactamente el tipo de diferencia que despues nadie puede reproducir.
 */

import {
  crearCentesimos,
  diasDesdeEpoca,
  fechaLocalDesdeTexto,
  formatear,
  type FechaLocal,
} from '@padel/domain';

const DIAS = ['domingo', 'lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado'] as const;

const MESES = [
  'enero',
  'febrero',
  'marzo',
  'abril',
  'mayo',
  'junio',
  'julio',
  'agosto',
  'setiembre',
  'octubre',
  'noviembre',
  'diciembre',
] as const;

/** Dia de la semana, 0 = domingo. El 1970-01-01 fue jueves, de ahi el 4. */
export function diaDeLaSemana(fecha: FechaLocal): number {
  return (((diasDesdeEpoca(fecha) + 4) % 7) + 7) % 7;
}

/** `"2026-08-11"` -> `"martes 11 de agosto"`. Devuelve el texto crudo si no parsea. */
export function fechaLarga(localFecha: string): string {
  const fecha = fechaLocalDesdeTexto(localFecha);
  if (!fecha.ok) {
    return localFecha;
  }
  const dia = DIAS[diaDeLaSemana(fecha.valor)] ?? '';
  const mes = MESES[fecha.valor.mes - 1] ?? '';
  return `${dia} ${String(fecha.valor.dia)} de ${mes}`;
}

/** `"2026-08-11"` -> `"mar 11"`. Para las pestanas de dia, donde no entra el nombre largo. */
export function fechaCorta(localFecha: string): { diaSemana: string; diaMes: string } {
  const fecha = fechaLocalDesdeTexto(localFecha);
  if (!fecha.ok) {
    return { diaSemana: '', diaMes: localFecha };
  }
  const dia = DIAS[diaDeLaSemana(fecha.valor)] ?? '';
  return { diaSemana: dia.slice(0, 3), diaMes: String(fecha.valor.dia) };
}

/** `80000` -> `"$ 800,00"`. Devuelve `"—"` si el importe no es representable. */
export function precio(centesimos: number): string {
  const importe = crearCentesimos(centesimos);
  if (!importe.ok) {
    return '—';
  }
  return formatear(importe.valor, { conSimbolo: true });
}

/** `60` -> `"60 minutos"`; `90` -> `"1 hora y 30 minutos"`. */
export function duracion(minutos: number): string {
  if (minutos === 90) {
    return '1 hora y 30 minutos';
  }
  if (minutos === 60) {
    return '1 hora';
  }
  return `${String(minutos)} minutos`;
}

/** Etiqueta de la intención de pago elegida al reservar. */
export function nombreDeMedioReserva(medio: MedioPagoReserva): string {
  return { efectivo: 'Efectivo', transferencia: 'Transferencia', combinado: 'Pago combinado' }[
    medio
  ];
}
